import { useEffect, useRef, useState } from 'react';
import { Modal, ModalContent } from '@nextui-org/react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { ArrowLeftRight, ChevronLeft, Trash2, X } from 'lucide-react';

import { numberFormatter } from '../../utils/calorieLog';
import { formatQuantity } from '../../utils/dietPlanView';
import { MAX_GRAMS, gramsToMatch, mealCalories, parseAmount, planFoodFromSearch, sliderMaxFor } from '../../utils/foodEdit';
import { searchedFoodName, useFoodSearch } from '../../utils/foodSearch';
import { planFoodFromResult } from '../../utils/dietImport';
import { amountOf, foodAtAmount } from '../../utils/portions';
import { useKeyboardInset } from '../../utils/useKeyboardInset';
import { AmountPicker, FoodFacts, FoodName, FoodResults, FoodSearchField } from '../calories/FoodPicker';

/**
 * What's inside the sheet; it mounts on every opening. Two ways to change the food: another one from the database in
 * its place (searched, then weighed: the grams start at what gives the old food's calories), or a new amount of it
 * (only a food weighed in g or ml). Both show what the meal comes to against what it was. Removing is a link in the
 * head, while the meal keeps another food.
 */
const SheetContent = ({ food, mealName, mealFoods, canRemove, onSwap, onAmount, onRemove, onAdd, substitutes, keepSource, close }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const ui = (key, options) => t(`pages.DietPlan.ui.${key}`, options);
  const tracker = (key, options) => t(`pages.CalorieTracker.ui.${key}`, options);
  const isDesktop = useMediaQuery({ query: '(min-width: 960px)' });
  const formatNumber = numberFormatter(language);

  // Adding a food to a meal being built: the same search and grams, with no food to change
  const isAdding = !food;
  const weighed = isAdding ? null : amountOf(food);
  const [tab, setTab] = useState('swap');
  const [query, setQuery] = useState('');
  const { results, isSearching, hasSearched } = useFoodSearch(query, language);
  // The database food picked to go in, with the grams that match the old food's calories
  const [picked, setPicked] = useState(null);
  const [grams, setGrams] = useState('');
  const [amount, setAmount] = useState(weighed ? String(weighed.amount) : '');

  const titleRef = useRef(null);
  const searchRef = useRef(null);
  const pickedTitleRef = useRef(null);
  const gramsRef = useRef(null);
  const amountRef = useRef(null);
  // Where the focus goes once the next view has rendered. Desktop starts in the search; phones wait for a tap, so the
  // keyboard doesn't open over the sheet as it slides up
  const focusNext = useRef(isDesktop ? 'search' : null);

  useEffect(() => {
    const target = focusNext.current;
    if (!target) return;
    focusNext.current = null;
    const select = (ref) => {
      ref.current?.focus();
      ref.current?.select();
    };
    if (target === 'search' && isDesktop) searchRef.current?.focus();
    // Back from a picked food: its back link is gone, so phones go to the sheet's title
    if (target === 'back') (isDesktop ? searchRef : titleRef).current?.focus();
    // Desktop types the grams; phones get the food read out and use the slider
    if (target === 'grams' && isDesktop) select(gramsRef);
    if (target === 'grams' && !isDesktop) pickedTitleRef.current?.focus();
    if (target === 'amount' && isDesktop) select(amountRef);
  });

  const lowerName = isAdding ? '' : language === 'pt' ? food.name.charAt(0).toLowerCase() + food.name.slice(1) : food.name;
  const plannedKcal = mealCalories(mealFoods);
  // Plans from outside the AI keep where each food came from (the table, an estimate) and its fiber
  const fromSearch = keepSource ? planFoodFromResult : planFoodFromSearch;

  // The food that would go in, or null while the amount isn't one
  let preview = null;
  if (picked) {
    const pickedGrams = parseAmount(grams);
    preview = pickedGrams && fromSearch(picked.food, pickedGrams, searchedFoodName(picked.food, language));
  } else if (tab === 'amount') {
    const newAmount = parseAmount(amount);
    preview = newAmount && foodAtAmount(food, newAmount);
  }
  const mealLine =
    preview &&
    (isAdding
      ? t('pages.DietImport.addMealLine', { meal: mealName, kcal: formatNumber(plannedKcal + preview.calories) })
      : ui('editMealLine', {
          meal: mealName,
          kcal: formatNumber(plannedKcal - (food?.calories || 0) + preview.calories),
          planned: formatNumber(plannedKcal),
        }));

  const switchTab = (next) => {
    setTab(next);
    focusNext.current = next === 'swap' ? 'search' : 'amount';
  };

  const pick = (result) => {
    const matched = isAdding ? 100 : gramsToMatch(result.calories, food.calories);
    setPicked({ food: result, matched });
    setGrams(String(matched));
    focusNext.current = 'grams';
  };

  const backToSearch = () => {
    setPicked(null);
    focusNext.current = 'back';
  };

  // The plan's own substitutions come first, in the nutritionist's amounts: one tap puts one in
  const renderSubstitutes = () =>
    substitutes?.length > 0 && (
      <section className="mt-fe-subs" aria-labelledby="mt-fe-subs-title">
        <h3 id="mt-fe-subs-title" className="mt-fe-subs__title">
          <ArrowLeftRight aria-hidden="true" />
          {t('pages.DietImport.subsTitle')}
        </h3>
        <ul className="mt-fe-subs__list">
          {substitutes.map((item, index) => (
            <li key={`${index}-${item.name}`}>
              <button type="button" className="mt-fe-sub" onClick={() => onSwap(item)}>
                <span className="mt-fe-sub__text">
                  <span className="mt-fe-sub__name">{item.name}</span>
                  <span className="mt-fe-sub__qty">{formatQuantity(item.quantity)}</span>
                </span>
                <span className="mt-fe-sub__kcal">{tracker('kcal', { kcal: formatNumber(item.calories) })}</span>
              </button>
            </li>
          ))}
        </ul>
        {!query.trim() && <p className="mt-fe-subs__or">{t('pages.DietImport.subsOrSearch')}</p>}
      </section>
    );

  const renderSearch = () =>
    query.trim() ? (
      <FoodResults results={results} isSearching={isSearching} hasSearched={hasSearched} onPick={pick} />
    ) : isAdding ? (
      <p className="mt-cal-sheet__msg">{t('pages.DietImport.addSearchHint', { meal: mealName })}</p>
    ) : (
      !substitutes?.length && <p className="mt-cal-sheet__msg">{ui('editSearchHint', { food: lowerName })}</p>
    );

  const renderPicked = () => (
    <div className="mt-cal-amount">
      <div className="mt-cal-amount__top">
        <button type="button" className="mt-cal-link" onClick={backToSearch}>
          <ChevronLeft aria-hidden="true" />
          {tracker('backToSearch')}
        </button>
      </div>
      <h3 className="mt-cal-amount__name" ref={pickedTitleRef} tabIndex={-1}>
        <FoodName name={searchedFoodName(picked.food, language)} />
      </h3>
      <p className="mt-cal-amount__per">{tracker('kcalPer100', { kcal: formatNumber(picked.food.calories) })}</p>
      <AmountPicker value={grams} onChange={setGrams} inputRef={gramsRef} label={ui('editAmountG')} max={sliderMaxFor(picked.matched)} limit={MAX_GRAMS} />
      <FoodFacts values={preview} />
      {mealLine && <p className="mt-fe-meal">{mealLine}</p>}
    </div>
  );

  const renderAmount = () => (
    <div className="mt-cal-amount mt-fe-amount">
      <AmountPicker
        value={amount}
        onChange={setAmount}
        inputRef={amountRef}
        unit={weighed.unit.toLowerCase()}
        label={ui(weighed.unit.toLowerCase() === 'ml' ? 'editAmountMl' : 'editAmountG')}
        max={sliderMaxFor(weighed.amount)}
        limit={MAX_GRAMS}
      />
      <FoodFacts values={preview} />
      {mealLine && <p className="mt-fe-meal">{mealLine}</p>}
    </div>
  );

  let body;
  if (picked) body = renderPicked();
  else if (tab === 'amount') body = renderAmount();
  else
    body = (
      <>
        {!isAdding && !query.trim() && renderSubstitutes()}
        {renderSearch()}
      </>
    );

  let footer = null;
  if (picked) {
    footer = (
      <button
        type="button"
        className="mt-btn mt-btn--primary mt-cal-btn-big"
        onClick={() => (isAdding ? onAdd(preview) : onSwap(preview))}
        disabled={!preview}
      >
        {isAdding ? t('pages.DietImport.addFoodButton') : ui('editSwap')}
      </button>
    );
  } else if (tab === 'amount') {
    const isUnchanged = preview && preview.quantity === food.quantity;
    footer = (
      <button type="button" className="mt-btn mt-btn--primary mt-cal-btn-big" onClick={() => onAmount(preview)} disabled={!preview || isUnchanged}>
        {ui('editSaveAmount')}
      </button>
    );
  }

  return (
    <>
      <div className="mt-cal-sheet__head">
        <span className="mt-cal-sheet__grab" aria-hidden="true" />
        <div className="mt-fe-head">
          <div className="mt-cal-sheet__top">
            <h2 className="mt-cal-sheet__title" ref={titleRef} tabIndex={-1}>
              {isAdding ? t('pages.DietImport.addFoodTitle') : food.name}
            </h2>
            <button type="button" className="mt-icon-btn" onClick={close} aria-label={t('standalone.close')}>
              <X aria-hidden="true" />
            </button>
          </div>
          <div className="mt-fe-current">
            <p className="mt-cal-sheet__sub">
              {isAdding ? mealName : [mealName, formatQuantity(food.quantity), tracker('kcal', { kcal: formatNumber(food.calories) })].join(' · ')}
            </p>
            {!isAdding && canRemove && !picked && (
              <button type="button" className="mt-fe-remove" onClick={onRemove}>
                <Trash2 aria-hidden="true" />
                {ui('editRemove')}
              </button>
            )}
          </div>
        </div>
        {weighed && !picked && (
          <div className="mt-segment mt-cal-sheet__tabs" role="group" aria-label={ui('editWhat')}>
            <button type="button" aria-pressed={tab === 'swap'} onClick={() => switchTab('swap')}>
              {ui('editTabSwap')}
            </button>
            <button type="button" aria-pressed={tab === 'amount'} onClick={() => switchTab('amount')}>
              {ui('editTabAmount')}
            </button>
          </div>
        )}
        {tab === 'swap' && !picked && <FoodSearchField inputRef={searchRef} value={query} onChange={setQuery} />}
      </div>
      <div className="mt-cal-sheet__body">{body}</div>
      {footer && <div className="mt-cal-sheet__foot">{footer}</div>}
    </>
  );
};

/**
 * Changing one food of a planned meal by hand: swap it for another from the database, change its amount, or take it
 * out. A bottom sheet on phones that rises over the keyboard; on desktop a dialog that grows with its content (the
 * page behind scrolls it, never a box inside it), its head and its button pinned in view.
 */
export const FoodEditSheet = ({
  isOpen,
  onOpenChange,
  food,
  mealName,
  mealFoods,
  canRemove,
  onSwap,
  onAmount,
  onRemove,
  onAdd,
  substitutes,
  keepSource = false,
}) => {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery({ query: '(min-width: 960px)' });
  const keyboard = useKeyboardInset(isOpen && !isDesktop);

  // Lifted over the keyboard, the sheet is shorter: the field being typed in stays in view
  useEffect(() => {
    if (!keyboard.inset) return;
    const field = document.activeElement;
    if (field?.closest?.('.mt-fe-sheet')) field.scrollIntoView({ block: 'nearest' });
  }, [keyboard.inset]);

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      placement={isDesktop ? 'top' : 'bottom'}
      scrollBehavior={isDesktop ? 'outside' : 'normal'}
      hideCloseButton
      aria-label={food?.name || t('pages.DietImport.addFoodTitle')}
      classNames={{ base: `mt-root mt-cal-sheet mt-fe-sheet${keyboard.inset ? ' is-keyboard' : ''}`, backdrop: 'mt-cal-backdrop' }}
      style={keyboard.inset ? { '--mt-kb': `${keyboard.inset}px`, '--mt-vvh': `${keyboard.height}px` } : undefined}
    >
      <ModalContent>
        {(close) =>
          (food || onAdd) && (
            <SheetContent
              food={food}
              mealName={mealName}
              mealFoods={mealFoods}
              canRemove={canRemove}
              onSwap={onSwap}
              onAmount={onAmount}
              onRemove={onRemove}
              onAdd={onAdd}
              substitutes={substitutes}
              keepSource={keepSource}
              close={close}
            />
          )
        }
      </ModalContent>
    </Modal>
  );
};
